import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormCheck } from '../hooks/useFormCheck';
import { requestFormCheckTts } from '../api/formCheckService';
import { useAudioCache } from '../hooks/useAudioCache';
import SwitchCameraIcon from './icons/SwitchCameraIcon';
import Modal from './Modal';
import { useLocale } from '../context/LocaleContext';

const StageLabel = {
  idle: 'idle',
  initializing: 'initializing',
  preview: 'preview',
  countdown: 'countdown',
  capturing: 'capturing',
  processing: 'processing',
  result: 'result',
  error: 'error'
};

const verdictBadgeStyles = {
  pass: 'bg-emerald-500',
  borderline: 'bg-amber-500',
  fail: 'bg-red-500'
};

export function getVerdictLabel(verdict, t) {
  if (verdict === 'pass') return t('formCheck.verdictPass', 'PASS');
  if (verdict === 'borderline') return t('formCheck.verdictBorderline', 'BORDERLINE');
  if (verdict === 'fail') return t('formCheck.verdictFail', 'FAIL');
  return t('formCheck.unknown', 'UNKNOWN');
}

export function getCameraFacingLabel(cameraFacing, t) {
  if (cameraFacing === 'environment') return t('formCheck.rear', 'Rear');
  if (cameraFacing === 'user') return t('formCheck.front', 'Front');
  return cameraFacing || t('formCheck.unknownLower', 'unknown');
}

export default function FormCheckPanel({ isOpen, onClose, exerciseName, exerciseDescription, onQuotaStateChange }) {
  const { locale, t } = useLocale();
  const {
    state,
    start,
    beginCapture,
    stopCountdown,
    toggleCamera,
    reset,
    consentGiven,
    cameraFacing,
    previewStream
  } = useFormCheck({
    exerciseName,
    exerciseDescription,
    onQuotaStateChange,
    locale
  });
  const {
    get: getCachedAudio,
    set: cacheAudio,
    has: hasCachedAudio
  } = useAudioCache();
  const [ttsLoading, setTtsLoading] = useState(false);
  const audioRef = useRef(null);
  const previewVideoRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const videoEl = previewVideoRef.current;
    if (!videoEl) return;
    if (!isOpen) {
      videoEl.srcObject = null;
      return;
    }
    if (previewStream) {
      videoEl.srcObject = previewStream;
    } else {
      videoEl.srcObject = null;
    }
  }, [isOpen, previewStream, state.stage]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (state.stage === StageLabel.result) {
      return;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (err) {
        // no-op
      }
    }
  }, [isOpen, state.stage]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (state.stage !== StageLabel.result) {
      return;
    }
    const tips = state.data?.tips_text;
    if (!tips) {
      return;
    }

    let active = true;

    const aurasAudio = async () => {
      if (hasCachedAudio(tips)) {
        const cachedAudio = getCachedAudio(tips);
        if (!cachedAudio) {
          return;
        }
        setTtsLoading(false);
        if (audioRef.current && audioRef.current !== cachedAudio) {
          try {
            audioRef.current.pause();
          } catch (err) {
            // no-op
          }
        }
        audioRef.current = cachedAudio;
        cachedAudio.currentTime = 0;
        try {
          await cachedAudio.play();
        } catch (err) {
          console.warn('[FormCheckPanel] Autoplay blocked or failed', err);
        }
        return;
      }

      setTtsLoading(true);
      try {
        const { audioUrl } = await requestFormCheckTts(tips);
        if (!audioUrl || !active) {
          return;
        }
        const audio = new Audio(audioUrl);
        cacheAudio(tips, audio);
        if (audioRef.current && audioRef.current !== audio) {
          try {
            audioRef.current.pause();
          } catch (err) {
            // no-op
          }
        }
        audioRef.current = audio;
        try {
          await audio.play();
        } catch (err) {
          console.warn('[FormCheckPanel] Autoplay blocked or failed', err);
        }
      } catch (err) {
        if (active) {
          console.error('[FormCheckPanel] TTS auto-fetch failed', err);
        }
      } finally {
        if (active) {
          setTtsLoading(false);
        }
      }
    };

    aurasAudio();

    return () => {
      active = false;
    };
  }, [cacheAudio, getCachedAudio, hasCachedAudio, isOpen, state.data?.tips_text, state.stage]);

  useEffect(() => {
    if (isOpen || state.stage === StageLabel.idle) {
      return;
    }
    reset();
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (err) {
        // no-op
      }
      audioRef.current = null;
    }
  }, [isOpen, reset, state.stage]);

  const facingModeTip = useMemo(() => {
    if (!cameraFacing) return null;
    if (cameraFacing === 'environment') return null;
    return t('formCheck.rearCameraTip', 'If possible, switch to the rear camera for a wider field of view.');
  }, [cameraFacing, t]);

  const cameraSwitching = Boolean(state.cameraSwitching);
  const cameraToggleError = state.cameraToggleError;
  const tipsText = state.data?.tips_text;
  const hasTipsAudioReady = tipsText ? hasCachedAudio(tipsText) : false;

  const progressPercent = state.stage === StageLabel.capturing
    ? Math.round((state.progress || 0) * 100)
    : 0;

  const verdict = state.data?.verdict;
  const verdictClass =
    verdict && verdictBadgeStyles[verdict]
      ? verdictBadgeStyles[verdict]
      : 'bg-slate-600';

  const handleStart = useCallback(() => {
    if (state.stage === StageLabel.idle || state.stage === StageLabel.error) {
      start();
    }
  }, [start, state.stage]);

  const handleBeginCapture = useCallback(() => {
    if (state.stage === StageLabel.preview) {
      beginCapture();
    }
  }, [beginCapture, state.stage]);

  const handleStopCountdown = useCallback(() => {
    if (state.stage === StageLabel.countdown) {
      stopCountdown();
    }
  }, [stopCountdown, state.stage]);

  const handleDismiss = useCallback(() => {
    reset();
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (err) {
        // no-op
      }
      audioRef.current = null;
    }
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, reset]);

  const handlePlayTips = useCallback(async () => {
    if (!tipsText) return;

    const cachedAudio = getCachedAudio(tipsText);
    if (cachedAudio) {
      if (audioRef.current && audioRef.current !== cachedAudio) {
        audioRef.current.pause();
      }
      audioRef.current = cachedAudio;
      cachedAudio.currentTime = 0;
      cachedAudio.play().catch((err) => {
        console.error('[FormCheckPanel] Failed to replay cached TTS audio', err);
      });
      return;
    }

    if (ttsLoading) return;

    setTtsLoading(true);
    try {
      const { audioUrl } = await requestFormCheckTts(tipsText);
      if (!audioUrl) {
        console.warn('[FormCheckPanel] No audio available for tips call.');
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(audioUrl);
      cacheAudio(tipsText, audio);
      audioRef.current = audio;
      audio.play().catch((err) => {
        console.error('[FormCheckPanel] Failed to play TTS audio', err);
      });
    } catch (err) {
      console.error('[FormCheckPanel] TTS request failed', err);
    } finally {
      setTtsLoading(false);
    }
  }, [cacheAudio, getCachedAudio, tipsText, ttsLoading]);

  const stats = state.data?.stats ?? {};
  const flags = Array.isArray(state.data?.flags) ? state.data.flags : [];
  const uploadStats = state.upload ?? {};

  const renderContent = () => {
    if (state.stage === StageLabel.initializing) {
      return (
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center text-sm text-gray-300">
          <div className="h-14 w-14 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
          <p>{t('formCheck.accessingCamera', 'Accessing camera... please allow permission if prompted.')}</p>
        </div>
      );
    }

    if (state.stage === StageLabel.preview || state.stage === StageLabel.countdown) {
      const isCountdown = state.stage === StageLabel.countdown;
      const secondsRemaining = state.seconds ?? 0;
      return (
        <div className="relative flex h-[720px] w-full flex-col overflow-hidden rounded-3xl bg-black">
          <video
            ref={previewVideoRef}
            className="absolute inset-0 h-full w-full bg-black object-contain"
            autoPlay
            playsInline
            muted
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-black/20" />
          <div className="relative z-10 flex items-center justify-between px-4 py-3 text-white">
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center gap-2 rounded-full bg-black/30 px-4 py-2 text-xs font-semibold uppercase tracking-wide backdrop-blur"
            >
              {t('formCheck.close', 'Close')}
            </button>
            <button
              type="button"
              aria-label={t('formCheck.switchCameraLens', 'Switch camera lens')}
              onClick={toggleCamera}
              disabled={cameraSwitching || isCountdown}
              className="inline-flex items-center gap-2 rounded-full bg-black/40 px-4 py-2 text-xs font-semibold text-white backdrop-blur transition hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SwitchCameraIcon className="h-4 w-4" />
              <span>{cameraSwitching ? t('formCheck.switching', 'Switching...') : t('formCheck.switchLens', 'Switch lens')}</span>
            </button>
          </div>
          {isCountdown && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-white">
              <span className="text-xs uppercase tracking-[0.4em] text-gray-200">{t('formCheck.startingIn', 'Starting in')}</span>
              <span className="mt-3 text-[6rem] font-bold leading-none drop-shadow-lg">{secondsRemaining}</span>
            </div>
          )}
          <div className="relative z-10 mt-auto w-full rounded-t-[2rem] bg-black/50 p-5 text-sm text-gray-100 backdrop-blur">
            <p>{t('formCheck.previewInstruction', 'Make sure your entire body fits in frame and lighting is bright. Perform the exercise for ~15 seconds after the countdown ends.')}</p>
            <div className="mt-3 space-y-1 text-xs">
              {cameraFacing && (
                <p className="text-gray-300">{t('formCheck.currentCamera', 'Current camera: {camera}', {
                  camera: getCameraFacingLabel(cameraFacing, t)
                })}</p>
              )}
              {facingModeTip && (
                <p className="text-amber-300">{facingModeTip}</p>
              )}
              {cameraToggleError && (
                <p className="text-red-300">{cameraToggleError}</p>
              )}
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={isCountdown ? handleStopCountdown : handleBeginCapture}
                className={`flex-1 rounded-2xl px-5 py-3 text-base font-semibold text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 ${isCountdown ? 'bg-red-600 hover:bg-red-700' : 'bg-cyan-500 hover:bg-cyan-600'}`}
              >
                {isCountdown ? t('formCheck.stopCountdown', 'Stop Countdown') : t('formCheck.beginCapture', 'Begin Capture')}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="flex-1 rounded-2xl border border-white/40 bg-white/5 px-5 py-3 text-base font-semibold text-white transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                {t('formCheck.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (state.stage === StageLabel.capturing) {
      return (
        <div className="relative flex h-[720px] w-full flex-col overflow-hidden rounded-3xl bg-black">
          <video
            ref={previewVideoRef}
            className="absolute inset-0 h-full w-full bg-black object-contain"
            autoPlay
            playsInline
            muted
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40" />
          <div className="relative z-10 flex items-center justify-between px-4 py-3 text-white">
            <span className="text-[0.65rem] uppercase tracking-[0.4em] text-white/70">{t('formCheck.capturing', 'Capturing')}</span>
            <span className="text-sm font-semibold">{progressPercent}%</span>
          </div>
          <div className="relative z-10 mt-auto w-full rounded-t-[2rem] bg-black/55 p-5 text-sm text-gray-100 backdrop-blur">
            <p>{t('formCheck.captureInstruction', 'Keep performing the movement naturally while we capture ~18 frames of your form.')}</p>
            <div className="mt-4">
              <div className="flex justify-between text-xs uppercase tracking-wide text-gray-400">
                <span>{t('formCheck.progress', 'Progress')}</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-cyan-500 transition-all duration-150 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            {facingModeTip && (
              <p className="mt-3 text-xs text-amber-300">
                {t('formCheck.facingModeHint', '{message} Browsers treat facingMode as a hint.', {
                  message: facingModeTip,
                })}
              </p>
            )}
            <p className="mt-3 text-xs text-gray-300">
              {t('formCheck.stayInFrame', "Stay within frame until the bar fills. We'll stop automatically once enough motion is captured.")}
            </p>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-full border border-white/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/80 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                {t('formCheck.cancelCapture', 'Cancel Capture')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (state.stage === StageLabel.processing) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center text-sm text-gray-300">
          <div className="h-14 w-14 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
          <p>{t('formCheck.analyzing', 'Analyzing posture... this should take just a moment.')}</p>
        </div>
      );
    }

    if (state.stage === StageLabel.error) {
      return (
        <div className="space-y-5">
          <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4">
            <h3 className="text-sm font-semibold text-red-200">{t('formCheck.couldNotComplete', 'Form check could not complete')}</h3>
            <p className="mt-2 text-sm text-red-100">{state.message}</p>
            {state.code === 'E_POSE_QUALITY_FAIL' && state.details && (
              <ul className="mt-3 space-y-1 text-xs text-red-100/90">
                <li>{t('formCheck.poseLandmarkScore', 'Pose landmark score: {score}', { score: state.details.landmarkOkRatio?.toFixed?.(2) ?? state.details.landmarkOkRatio })}</li>
                <li>{t('formCheck.lightingScore', 'Lighting score: {score}', { score: state.details.averageLuma != null ? state.details.averageLuma.toFixed(2) : 'n/a' })}</li>
                <li>{t('formCheck.poseQualityTip', 'Tip: Position the camera farther back so shoulders and hips stay visible.')}</li>
              </ul>
            )}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={reset}
              className="flex-1 rounded-2xl bg-cyan-500 px-5 py-3 text-base font-semibold text-white transition hover:bg-cyan-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
            >
              {t('formCheck.tryAgain', 'Try Again')}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="flex-1 rounded-2xl border border-gray-600 bg-transparent px-5 py-3 text-base font-semibold text-gray-200 transition hover:bg-gray-700/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
            >
              {t('formCheck.close', 'Close')}
            </button>
          </div>
        </div>
      );
    }

    if (state.stage === StageLabel.result) {
      return (
        <div className="space-y-5">
          <div className="flex flex-col items-center gap-3 text-center">
            <span
              className={`inline-flex items-center rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wide ${verdictClass}`}
            >
              {getVerdictLabel(verdict, t)}
            </span>
            {tipsText ? (
              <p className="text-sm leading-relaxed text-gray-100">{tipsText}</p>
            ) : (
              <p className="text-sm text-gray-300">{t('formCheck.noAdditionalTips', "We didn't receive additional tips from the provider.")}</p>
            )}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handlePlayTips}
              disabled={ttsLoading}
              className="flex-1 rounded-2xl bg-cyan-500 px-5 py-3 text-base font-semibold text-white transition hover:bg-cyan-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {ttsLoading
                ? t('formCheck.preparingAudio', 'Preparing audio...')
                : hasTipsAudioReady
                  ? t('formCheck.replayTips', 'Replay Tips')
                  : t('formCheck.playTips', 'Play Tips')}
            </button>
            <button
              type="button"
              onClick={reset}
              className="flex-1 rounded-2xl border border-gray-600 bg-transparent px-5 py-3 text-base font-semibold text-gray-200 transition hover:bg-gray-700/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
            >
              {t('formCheck.runAgain', 'Run Again')}
            </button>
          </div>
          <details className="rounded-2xl border border-gray-700 bg-gray-800/60 p-4 text-sm text-gray-200">
            <summary className="cursor-pointer select-none text-base font-semibold text-white">
              {t('formCheck.whatWeMeasured', 'What we measured')}
            </summary>
            <div className="mt-3 space-y-1 text-sm text-gray-300">
              <p>{t('formCheck.framesUsed', 'Frames used: {value}', { value: stats.frames_used ?? 'n/a' })}</p>
              <p>{t('formCheck.landmarkOkRatio', 'Landmark OK ratio: {value}', { value: stats.landmark_ok_ratio ?? 'n/a' })}</p>
              <p>{t('formCheck.provider', 'Provider: {value}', { value: stats.provider ?? 'n/a' })}</p>
              <p>{t('formCheck.flags', 'Flags: {value}', { value: flags.length ? flags.join(', ') : t('formCheck.none', 'none') })}</p>
              {uploadStats.bytes != null && (
                <p>{t('formCheck.uploadedBytes', 'Uploaded bytes (est.): {value} KB', { value: Math.round(uploadStats.bytes / 1024) })}</p>
              )}
              <p>{t('formCheck.camera', 'Camera: {value}', {
                value: getCameraFacingLabel(uploadStats.cameraFacing || cameraFacing, t)
              })}</p>
            </div>
          </details>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-full rounded-2xl bg-gray-100 px-5 py-3 text-base font-semibold text-gray-900 transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            {t('formCheck.closeFormCheck', 'Close Form Check')}
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-6 text-center text-white">
        <div className="flex h-28 w-28 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300">
          <span className="text-3xl font-semibold tracking-widest">AI</span>
        </div>
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight">{t('formCheck.title', 'AI Form Check')}</h2>
          <p className="text-sm text-gray-300 max-w-md">
            {t('formCheck.description', 'Record your exercise to get AI-generated observations on your form. Results are for informational purposes and do not replace professional coaching.')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleStart}
          className="rounded-2xl bg-cyan-500 px-6 py-3 text-base font-semibold text-gray-900 transition hover:bg-cyan-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
        >
          {t('formCheck.startRecording', 'Start Recording')}
        </button>
        <div className="flex items-start justify-center gap-2 text-xs text-gray-400 max-w-md">
          <div className="mt-1 h-2.5 w-2.5 rounded-full border border-gray-500"></div>
          <p>{t('formCheck.privacyNote', 'We process a few camera frames with secure AI. We keep scores only and do not store images.')}</p>
        </div>
        {!consentGiven && (
          <p className="text-xs text-amber-300">
            {t('formCheck.consentNote', 'By starting Form Check you consent to off-device processing for posture analysis.')}
          </p>
        )}
      </div>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      title={t('formCheck.modalTitle', 'Form Check')}
      size="lg"
      backdropClassName="z-[60]"
      containerClassName="!bg-[#1d2127]"
    >
      <div className="flex flex-col gap-6 text-white">{renderContent()}</div>
    </Modal>
  );
}
